// Minimal promisified IndexedDB used only by the offline queue. Two stores: 'ops'
// (pending operations) and 'dead' (the dead-letter store). Degrades to throwing —
// callers treat IDB failure as "not durable" and say so, never silently.
const DB_NAME = 'deltat-v2';
const DB_VERSION = 1;
export const OPS = 'ops';
export const DEAD = 'dead';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(OPS)) db.createObjectStore(OPS, { keyPath: 'opId' });
      if (!db.objectStoreNames.contains(DEAD)) db.createObjectStore(DEAD, { keyPath: 'opId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb open failed'));
  });
  return dbPromise;
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = run(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('idb op failed'));
      }),
  );
}

export const idbPut = (store: string, value: unknown): Promise<IDBValidKey> =>
  tx(store, 'readwrite', (s) => s.put(value));
export const idbDelete = (store: string, key: string): Promise<undefined> =>
  tx(store, 'readwrite', (s) => s.delete(key) as IDBRequest<undefined>);
export const idbAll = <T>(store: string): Promise<T[]> =>
  tx(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
export const idbGet = <T>(store: string, key: string): Promise<T | undefined> =>
  tx(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
